import {
	NATIVE_EMBED_EXTENSION_KEY,
	NATIVE_EMBED_EXTENSION_TYPE,
} from '@atlaskit/editor-common/extensions';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { findSelectedNodeOfType } from '@atlaskit/editor-prosemirror/utils';
import { getParameters, type AlignmentValue } from '@atlaskit/native-embeds-common';

export interface NativeEmbedsPluginState {
	isResizing: boolean;
	resizingWidth?: number;
	selectedEmbedAlignment: AlignmentValue | null;
	selectedEmbedPos: number | null;
	showUrlToolbar: boolean;
}

type NativeEmbedsPluginAction =
	| { type: 'SHOW_URL_TOOLBAR' }
	| { type: 'HIDE_URL_TOOLBAR' }
	| { isResizing: boolean; type: 'SET_RESIZING'; width?: number };

export const NATIVE_EMBEDS_IS_RESIZING_KEY = 'nativeEmbedsPlugin.isResizing';

export const pluginKey = new PluginKey<NativeEmbedsPluginState>('nativeEmbedsPlugin');

export const createPlugin = () =>
	new SafePlugin<NativeEmbedsPluginState>({
		key: pluginKey,
		state: {
			init: (): NativeEmbedsPluginState => ({
				isResizing: false,
				selectedEmbedAlignment: null,
				selectedEmbedPos: null,
				showUrlToolbar: false,
			}),
			apply: (tr, state, _oldEditorState, newEditorState): NativeEmbedsPluginState => {
				const meta = tr.getMeta(pluginKey) as NativeEmbedsPluginAction | undefined;
				let nextState = state;
				if (meta?.type === 'SHOW_URL_TOOLBAR') {
					nextState = { ...nextState, showUrlToolbar: true };
				}
				if (meta?.type === 'HIDE_URL_TOOLBAR') {
					nextState = { ...nextState, showUrlToolbar: false };
				}
				if (meta?.type === 'SET_RESIZING') {
					nextState = {
						...nextState,
						isResizing: meta.isResizing,
						resizingWidth: meta.width,
					};
				}
				const { schema, selection } = newEditorState;
				const selected = findSelectedNodeOfType(schema.nodes.extension)(selection);
			const isNativeEmbed =
				selected &&
				selected.node.attrs?.extensionType === NATIVE_EMBED_EXTENSION_TYPE &&
				selected.node.attrs?.extensionKey?.includes(NATIVE_EMBED_EXTENSION_KEY);

				if (isNativeEmbed) {
					const newPos = selected.pos;
					const newAlignment = getParameters(
						selected.node.attrs.parameters,
						'alignment',
					) as AlignmentValue;

					if (
						newPos !== nextState.selectedEmbedPos ||
						newAlignment !== nextState.selectedEmbedAlignment
					) {
						nextState = {
							...nextState,
							selectedEmbedPos: newPos,
							selectedEmbedAlignment: newAlignment,
						};
					}
				} else if (nextState.selectedEmbedPos !== null) {
					nextState = {
						...nextState,
						selectedEmbedPos: null,
						selectedEmbedAlignment: null,
					};
				}

				return nextState;
			},
		},
	});
